import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { useLocale } from '../i18n/LocaleContext';

function CustomDomainPanel() {
  const { t, dateLocale } = useLocale();
  const [configuration, setConfiguration] = useState(null);
  const [domains, setDomains] = useState(null);
  const [projects, setProjects] = useState(null);
  const [label, setLabel] = useState('');
  const [durationYears, setDurationYears] = useState(1);
  const [projectId, setProjectId] = useState('');
  const [availability, setAvailability] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [domainResult, projectResult] = await Promise.all([api.customDomains(), api.projects()]);
      setConfiguration(domainResult.configuration);
      setDomains(domainResult.domains);
      const deployable = (projectResult.projects || []).filter(project => project.urls?.live || project.deployUrl);
      setProjects(deployable);
      if (!projectId && deployable[0]) setProjectId(deployable[0].id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('domain.loadError'));
      setDomains([]);
      setProjects([]);
    }
  }, [projectId, t]);

  useEffect(() => { load(); }, [load]);

  const normalizedLabel = label.trim().toLowerCase();
  const previewHost = normalizedLabel && configuration?.baseDomain ? `${normalizedLabel}.${configuration.baseDomain}` : `your-app.${configuration?.baseDomain || 'nexusaiblog.com'}`;
  const selectedPrice = useMemo(() => configuration?.prices?.find(price => price.durationYears === durationYears), [configuration, durationYears]);
  const pricesConfigured = Boolean(selectedPrice?.amount > 0);

  const checkAvailability = async () => {
    setError('');
    setAvailability(null);
    if (!normalizedLabel) { setError(t('domain.subdomainRequired')); return; }
    setLoading(true);
    try {
      setAvailability(await api.customDomainAvailability(normalizedLabel));
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('domain.availabilityError'));
    } finally {
      setLoading(false);
    }
  };

  const startCheckout = async () => {
    setError('');
    if (!projectId) { setError(t('domain.projectRequired')); return; }
    if (!availability?.available || availability.label !== normalizedLabel) { setError(t('domain.availabilityRequired')); return; }
    if (!pricesConfigured) { setError(t('domain.paymentUnavailable')); return; }
    setLoading(true);
    try {
      const result = await api.customDomainCheckout({
        projectId,
        label: normalizedLabel,
        durationYears,
        successUrl: `${window.location.origin}/?domain-purchase=success`,
        cancelUrl: `${window.location.origin}/?domain-purchase=cancel`
      });
      window.location.assign(result.url);
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('domain.checkoutError'));
      setLoading(false);
    }
  };

  const copyLink = async (url) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard access is unavailable');
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      window.setTimeout(() => setCopiedUrl(current => current === url ? '' : current), 1800);
    } catch (_) {
      setError(t('domain.copyError'));
    }
  };

  return (
    <div className="domain-center">
      <div className="domain-header">
        <div className="section-heading">{t('domain.title')}</div>
        <p className="subtitle">{t('domain.description', { domain: configuration?.baseDomain || 'nexusaiblog.com' })}</p>
      </div>
      <div className="domain-card">
        <div className="form-group">
          <label htmlFor="custom-subdomain">{t('domain.subdomain')}</label>
          <div className="domain-subdomain-row">
            <input id="custom-subdomain" value={label} onChange={event => { setLabel(event.target.value); setAvailability(null); }} placeholder="my-store" autoComplete="off" />
            <button type="button" className="btn domain-check-btn" onClick={checkAvailability} disabled={loading}>{t('domain.checkAvailability')}</button>
          </div>
          <small style={{ display: 'block', marginTop: '0.4rem', color: 'var(--text-secondary)' }}>https://{previewHost}</small>
          {availability && <div className={`domain-availability ${availability.available ? 'available' : 'unavailable'}`}>{availability.available ? t('domain.available', { host: availability.host }) : t('domain.unavailable')}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="domain-project">{t('domain.activeProject')}</label>
          <select id="domain-project" value={projectId} onChange={event => setProjectId(event.target.value)}>
            <option value="">{t('domain.selectProject')}</option>
            {(projects || []).map(project => <option key={project.id} value={project.id}>{project.name} — {project.urls?.live || project.deployUrl}</option>)}
          </select>
          {projects?.length === 0 && <small style={{ display: 'block', marginTop: '0.4rem', color: 'var(--text-secondary)' }}>{t('domain.noProjects')}</small>}
        </div>

        <div className="form-group">
          <label>{t('domain.duration')}</label>
          <div className="domain-duration-grid">
            {[1, 2, 3].map(years => {
              const price = configuration?.prices?.find(item => item.durationYears === years);
              const selected = durationYears === years;
              return <button type="button" key={years} className={`domain-duration-card ${selected ? 'selected' : ''}`} aria-pressed={selected} onClick={() => setDurationYears(years)}>
                <span className="domain-duration-label">{t(`domain.${years === 1 ? 'oneYear' : years === 2 ? 'twoYears' : 'threeYears'}`)}</span>
                <span className="domain-duration-price">{Number.isInteger(price?.amount) && price.amount > 0 ? new Intl.NumberFormat(dateLocale, { style: 'currency', currency: String(configuration?.currency || 'usd').toUpperCase(), maximumFractionDigits: 2 }).format(price.amount / 100) : t('domain.notConfigured')}</span>
              </button>;
            })}
          </div>
        </div>
        {error && <div className="auth-error" style={{ marginTop: '0.75rem' }}>{error}</div>}
        <button type="button" className="submit-btn domain-buy-btn" onClick={startCheckout} disabled={loading || !pricesConfigured || !availability?.available}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          {loading ? t('domain.processing') : t('domain.buy', { duration: t(`domain.${durationYears === 1 ? 'oneYear' : durationYears === 2 ? 'twoYears' : 'threeYears'}`) })}
        </button>
      </div>

      <div className="domain-card domain-addresses-card">
        <div className="section-heading">{t('domain.yourAddresses')}</div>
        <div className="activity-list">
        {domains === null && <div className="no-logs">{t('domain.loading')}</div>}
        {domains?.length === 0 && <div className="no-logs">{t('domain.empty')}</div>}
        {domains?.map(domain => (
          <div className="activity-item" key={domain.id}>
            <span className="activity-time">{domain.status === 'active' ? t('domain.active') : domain.status}</span>
            <span className="activity-message">
              <strong>{domain.host}</strong> · {domain.projectName} · {domain.expiresAt ? t('domain.validUntil', { date: new Date(domain.expiresAt).toLocaleDateString(dateLocale) }) : t('domain.pendingPayment')}
              {domain.status === 'active' && (
                <span style={{ display: 'grid', gap: '0.45rem', marginTop: '0.65rem' }}>
                  {(domain.links || []).map(link => (
                    <span key={link.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <strong>{link.label}</strong>
                      <a href={link.url} target="_blank" rel="noreferrer" style={{ overflowWrap: 'anywhere' }}>{link.url}</a>
                      <button type="button" className="btn domain-copy-btn" onClick={() => copyLink(link.url)}>{copiedUrl === link.url ? t('domain.copied') : t('domain.copy')}</button>
                    </span>
                  ))}
                </span>
              )}
            </span>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}

export default CustomDomainPanel;
